import { useMemo, useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Search } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/clientWeb/components/ui/dialog"
import { Button } from "@/clientWeb/components/ui/button"
import { Checkbox } from "@/clientWeb/components/ui/checkbox"
import { useDirectorySearch, type DirectoryRow } from "./hooks/useDirectorySearch"
import { DirectoryScopeTabs } from "./directory-scope-tabs"
import { LoadMoreSentinel } from "./load-more-sentinel"
import { toggleSelected } from "./selection-map"
import type { SearchScope } from "@/features/meetings/services/contacts.api"

const scopeLabel: Record<SearchScope, string> = {
  subId: 'sub-Organization',
  scopeId: 'Organization',
  global: 'Global',
}

type AddMembersDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** User ids already in the group — filtered out of the candidate list. */
  existingMemberIds: string[]
  /** Confirm handler — receives the selected contactPresenceIds. */
  onAdd: (userIds: string[]) => void | Promise<void>
}

export function AddMembersDialog({ open, onOpenChange, existingMemberIds, onAdd }: AddMembersDialogProps) {
  const t = useTranslation()
  const [search, setSearch] = useState("")
  const [scope, setScope] = useState<SearchScope>("global")
  // Map, not an id array: the candidate list is replaced wholesale on every
  // scope/search/page change, so a ticked row has to be remembered on its own
  // rather than re-derived from whatever page happens to be loaded.
  const [selectedMap, setSelectedMap] = useState<Map<string, DirectoryRow>>(new Map())
  const [submitting, setSubmitting] = useState(false)

  // `existingMemberIds` arrives as a fresh array from the parent on every
  // render (`members.map(...)`), so it can never be used as a hook dependency
  // directly: it would never be equal to itself and `useDirectorySearch`
  // would refetch on every render. Keyed on the sorted, joined ids instead:
  // that string only changes when the membership actually does, so the `Set`
  // built from it (and the dependency it drives) stays stable in between.
  const existingKey = useMemo(() => [...existingMemberIds].sort().join(","), [existingMemberIds])
  const excludeIds = useMemo(
    () => new Set(existingKey ? existingKey.split(",") : []),
    [existingKey],
  )

  const {
    contacts: candidates,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    resultScope,
  } = useDirectorySearch({ enabled: open, scope, query: search, excludeIds })

  const toggleSelection = (row: DirectoryRow) => {
    setSelectedMap(prev => toggleSelected(prev, row))
  }

  const handleAdd = async () => {
    if (selectedMap.size === 0) return
    setSubmitting(true)
    try {
      await onAdd(Array.from(selectedMap.keys()))
      setSelectedMap(new Map())
      setSearch("")
      onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedMap(new Map())
      setSearch("")
      setScope("global")
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} size="sm">
      <DialogContent className="p-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <DialogTitle className="text-base">{t("presence.addMembers")}</DialogTitle>
          <DialogDescription>{t("presence.addMembersDesc")}</DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("presence.searchMembers")}
              className="w-full h-10 rounded-md border border-border bg-background pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <DirectoryScopeTabs value={scope} onChange={setScope} />

          <div className="max-h-72 overflow-y-auto rounded-md border border-border divide-y divide-border mt-3">
            {loading && candidates.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">{t("common.loading")}</div>
            ) : candidates.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">{t("presence.noMembersFound")}</div>
            ) : (
              <>
                {resultScope && resultScope !== scope && (
                  <p className="px-3 py-1.5 text-xs text-muted-foreground">
                    {t("presence.widerScopeNote", { scope: scopeLabel[resultScope] })}
                  </p>
                )}
                {candidates.map((member) => {
                  const checked = selectedMap.has(member.id)
                  return (
                    <label
                      key={member.id}
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/40 transition-colors"
                    >
                      <Checkbox
                        id={`add-member-${member.id}`}
                        checked={checked}
                        onCheckedChange={() => toggleSelection(member)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{member.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{member.subtitle}</p>
                        {member.contactOrgName && member.contactOrgName !== member.subtitle && (
                          <p className="text-xs text-muted-foreground truncate">{member.contactOrgName}</p>
                        )}
                      </div>
                    </label>
                  )
                })}
                <LoadMoreSentinel hasMore={hasMore} loading={loadingMore} onVisible={loadMore} />
              </>
            )}
          </div>
        </div>

        <DialogFooter className="px-5 py-4 border-t border-border">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleAdd} disabled={selectedMap.size === 0 || submitting}>
            {t("presence.addMembersBtn")} {selectedMap.size > 0 ? `(${selectedMap.size})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
